import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Checkbox, Select, TextField, Radio, MaskInput } from '../../../components/ReactHookForm';
import { phoneAndEmailTypeFormatter, genericFormatter } from '../../../utils/members/fieldsFormatters';
import { useGetStates } from '../../../hooks/useStatesList';
import { phonetype } from '../../../utils/members/options';

export default function ContactInformation() {
  const { watch, setValue } = useFormContext();
  const isReadOnly: boolean = watch('readOnly');
  const isMailingAddressDifferent: boolean = watch('isMailingAddressDifferent');

  const { data: states, isLoading } = useGetStates();

  useEffect(() => {
    // Clear out mailing address fields when isMailingAddressDifferent is unchecked
    if (!isMailingAddressDifferent) {
      setValue('mailingAddress.addressLine1', '');
      setValue('mailingAddress.addressLine2', '');
      setValue('mailingAddress.city', '');
      setValue('mailingAddress.stateProvince', '');
      setValue('mailingAddress.postal', '');
    }
  }, [isMailingAddressDifferent, setValue]);

  return (
    <>
      <div className="flex flex-col">
        <div className="flex justify-between">
          <h2 className="mb-4">Home address</h2>
          {!isReadOnly && <p>*Required</p>}
        </div>
        <div className="grid gap-4" style={{ gridTemplateColumns: '1.5fr 1.5fr 1.5fr 1fr 1fr' }}>
          <TextField name="homeAddress.addressLine1" label="Address Line 1" placeholder="Enter address" required />
          <TextField name="homeAddress.addressLine2" label="Address Line 2" placeholder="Enter address" />
          <TextField name="homeAddress.city" label="City" placeholder="Enter city" required />
          <Select
            name="homeAddress.stateProvince"
            label="State/Province"
            placeholder="Select state"
            options={states}
            isLoading={isLoading}
            required
            readOnlyFieldFormatter={genericFormatter}
          />
          <TextField name="homeAddress.postal" label="Zip/Postal Code" placeholder="10988" required />
        </div>
        {!isReadOnly && (
          <Checkbox
            name="isMailingAddressDifferent"
            label="Mailing address is different from home address"
            className="mb-4"
          />
        )}
      </div>
      {watch('isMailingAddressDifferent') && (
        <div className="flex flex-col">
          <h2 className="mb-4">Mailing Address</h2>
          <div className="grid gap-4" style={{ gridTemplateColumns: '1.5fr 1.5fr 1.5fr 1fr 1fr' }}>
            <TextField
              name="mailingAddress.addressLine1"
              label="Address Line 1"
              placeholder="Enter address"
              requiredIf={['mailingAddress.city', 'mailingAddress.stateProvince', 'mailingAddress.postal']}
            />
            <TextField name="mailingAddress.addressLine2" label="Address Line 2" placeholder="Enter address" />
            <TextField
              name="mailingAddress.city"
              label="City"
              placeholder="Enter city"
              requiredIf={['mailingAddress.addressLine1', 'mailingAddress.stateProvince', 'mailingAddress.postal']}
            />
            <Select
              name="mailingAddress.stateProvince"
              label="State/Province"
              placeholder="Select state"
              options={states}
              isLoading={isLoading}
              requiredIf={['mailingAddress.addressLine1', 'mailingAddress.city', 'mailingAddress.postal']}
              readOnlyFieldFormatter={genericFormatter}
            />
            <TextField
              name="mailingAddress.postal"
              label="Zip/Postal Code"
              placeholder="10988"
              requiredIf={['mailingAddress.addressLine1', 'mailingAddress.city', 'mailingAddress.stateProvince']}
            />
          </div>
        </div>
      )}
      <div className="flex flex-col">
        <h2 className="mb-4">Phone and Email</h2>
        <div className="grid gap-4 grid-cols-5">
          <Select
            name="phone1.phoneFaxTypeCd"
            label="Type"
            placeholder="Home..."
            options={phonetype}
            required
            readOnlyFieldFormatter={phoneAndEmailTypeFormatter}
          />
          <MaskInput name="phone1.phoneNumber" label="Phone number" placeholder="Enter phone number" required />
          <TextField name="phone1.extension" label="Ext." />
          {!isReadOnly && <Radio name="phoneIsPreferred" label="Set as primary" value="phone1" id="phone1" />}
        </div>
        <div className="grid gap-4 grid-cols-5">
          <Select
            name="phone2.phoneFaxTypeCd"
            label="Type"
            placeholder="Home..."
            options={phonetype}
            requiredIf={['phone2.phoneNumber']}
            readOnlyFieldFormatter={phoneAndEmailTypeFormatter}
          />
          <MaskInput
            name="phone2.phoneNumber"
            label="Phone number"
            placeholder="Enter phone number"
            requiredIf={['phone2.phoneFaxTypeCd']}
          />
          <TextField name="phone2.extension" label="Ext." />
          {!isReadOnly && <Radio name="phoneIsPreferred" label="Set as primary" value="phone2" id="phone2" />}
        </div>
        <div className="grid gap-4 grid-cols-3">
          <TextField name="contactEmail" label="Email" placeholder="Enter email" />
        </div>
      </div>
    </>
  );
}
